import { useEffect, useRef, useState } from 'react';
import QrScanner from 'qr-scanner';

import { trackCodeLoginEvent } from '@/shared/features/code-login/utils';
import { isInAppBrowser } from '@/utils/environment/isInAppBrowser';

/** getUserMedia 실패 원인을 reason 값으로 분류한다.
 * - 인앱 브라우저(카카오톡 등): navigator.getUserMedia 가 없거나 차단 → in_app_browser
 * - 권한 거부(NotAllowedError / SecurityError): permission_denied
 * - 카메라 없음(NotFoundError / OverconstrainedError) 또는 기타: no_camera
 */
function classifyQrCameraReason(
  error: unknown,
): 'permission_denied' | 'no_camera' | 'in_app_browser' {
  if (isInAppBrowser()) return 'in_app_browser';
  const name =
    error instanceof DOMException ||
    (error !== null && typeof error === 'object' && 'name' in error)
      ? String((error as { name: string }).name)
      : '';
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return 'permission_denied';
  }
  return 'no_camera';
}

/**
 * 페이지 내에서 직접 카메라를 켜 QR 을 스캔하는 훅.
 * OS 카메라 앱이 아니라 getUserMedia + qr-scanner 로 화면 안에서 스캔한다.
 *
 * `available === false` 이면 폴백: 카메라 권한 거부 / 카메라 없음 /
 * 인앱 브라우저(카카오톡 등)에서 카메라 접근 불가 / 스캐너 초기화 실패.
 * 이 경우 스캐너를 숨기고 수동 코드 입력 폼만 노출한다.
 *
 * 결과는 raw 텍스트 그대로 onScan 으로 넘긴다. 파싱/검증은 handleScan 이 담당한다.
 */
export function useQrScanner(onScan: (text: string) => void) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [available, setAvailable] = useState(true);

  // 최신 onScan 을 ref 로 들고 스캐너 콜백은 항상 ref 를 통해 호출한다.
  // onScan(=handleScan)은 react-query mutation 의존으로 매 렌더 identity 가 바뀌는데,
  // 이를 effect 의존성에 두면 렌더마다 스캐너를 destroy/재생성 → start()→재렌더 →
  // 무한 재초기화(React #185 Maximum update depth)로 에러 바운더리가 떴다.
  const onScanRef = useRef(onScan);
  useEffect(() => {
    onScanRef.current = onScan;
  }, [onScan]);

  // 스캐너는 마운트 시 1회만 초기화한다(콜백 identity 변경에 재시작하지 않음).
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // highlightScanRegion 은 라이브러리가 그리는 스캔영역 테두리 — 시안엔 없고
    // QR 가이드 오버레이(qr-scan-overlay.svg)가 그 역할을 하므로 끈다(이중 프레임 방지).
    // 시각 표시만 끄는 옵션이라 스캔 동작에는 영향 없다.
    const scanner = new QrScanner(
      video,
      result => onScanRef.current(result.data),
      {
        highlightScanRegion: false,
        // 모바일 후면 카메라 우선(전면이면 초점/화질이 떨어져 인식률 하락). 기본값이지만 명시.
        preferredCamera: 'environment',
        // 기본 scanRegion 은 비디오 중앙 min(w,h)*2/3 정사각형을 400px 로 downscale 해 디코드한다.
        // 영역이 좁아 가장자리·작은 QR 을 놓치고, downscale 이 작아 작은 QR 이 뭉개진다.
        // 영역을 0.9 로 넓히고 디코드 해상도를 500px 로 올려 OS 카메라앱 대비 약한 인식률을 보완한다.
        // (iOS 포함 라이브러리가 처리하는 옵션이라 미지원 환경에서도 에러 없이 동작)
        calculateScanRegion: video => {
          const base = Math.min(video.videoWidth, video.videoHeight);
          const size = Math.round(base * 0.9);
          return {
            x: Math.round((video.videoWidth - size) / 2),
            y: Math.round((video.videoHeight - size) / 2),
            width: size,
            height: size,
            downScaledWidth: 500,
            downScaledHeight: 500,
          };
        },
      },
    );

    let cancelled = false;
    // start() 가 거부되면(권한 거부 / 카메라 없음 / 초기화 실패) 폴백하고 이벤트를 발화한다.
    scanner.start().catch((error: unknown) => {
      if (!cancelled) {
        setAvailable(false);
        trackCodeLoginEvent('fail_qr_camera', {
          reason: classifyQrCameraReason(error),
        });
      }
    });

    return () => {
      cancelled = true;
      scanner.stop();
      scanner.destroy();
    };
  }, []);

  return { videoRef, available };
}
